<template>
  <v-form ref="form" v-model="valid" validate-on="input">
    <div class="subtitle1">{{ $t("modals.addSyslog.generalSettings") }}</div>
    <v-text-field
      v-model="localValue.name"
      class="mt-6"
      :label="localValue.name ? $t('modals.addSyslog.serverName') : ''"
      :aria-label="$t('modals.addSyslog.serverName')"
      :rules="[required()]"
      variant="outlined"
      maxlength="100"
      :placeholder="$t('modals.addSyslog.serverName')"
    />
    <div class="d-flex mt-6">
      <v-text-field
        v-model="localValue.destination"
        :rules="[required()]"
        :label="localValue.destination ? $t('modals.addSyslog.destination') : ''"
        :aria-label="$t('modals.addSyslog.destination')"
        variant="outlined"
        :placeholder="$t('modals.addSyslog.destination')"
      />
      <v-text-field
        v-model="localValue.port"
        class="ml-4 port-input"
        :label="localValue.port ? $t('modals.addSyslog.port') : ''"
        :aria-label="$t('modals.addSyslog.port')"
        variant="outlined"
        :rules="[required()]"
        :placeholder="$t('modals.addSyslog.port')"
      />
    </div>
    <div class="subtitle1 mt-8">{{ $t("modals.addSyslog.protocolSettings") }}</div>
    <div class="caption">{{ $t("modals.addSyslog.protocolSettingsSubtitle") }}</div>

    <v-text-field
      model-value="RFC5424"
      class="mt-6"
      :label="$t('modals.addSyslog.syslogProtocol')"
      :aria-label="$t('modals.addSyslog.syslogProtocol')"
      variant="outlined"
      disabled
      :placeholder="$t('modals.addSyslog.syslogProtocol')"
    />
    <div class="subtitle2 mb-4 mt-8">{{ $t("modals.addSyslog.certificate") }}</div>
    <draggable-file-uploader
      accept=".pem,.crt,.cer,.key"
      :error-message="fileUploadErrorMessage"
      :class="{ 'error-border': !!fileUploadErrorMessage }"
      :description="$t(`modals.addSyslog.clickToUpload`)"
      :subtitle="$t(`modals.addSyslog.fileType`)"
      @file-changed="handleFileUpload"
    />

    <div class="caption text-primary mt-2">{{ $t("modals.addSyslog.certificateHintLine1") }}</div>
    <div class="caption text-primary">{{ $t("modals.addSyslog.certificateHintLine2") }}</div>
    <div class="caption text-primary">{{ $t("modals.addSyslog.certificateHintLine3") }}</div>

    <div class="subtitle1 mt-6">{{ $t("modals.addSyslog.selectModules") }}</div>
    <div class="caption mb-4">{{ $t("modals.addSyslog.selectModulesSubtitle") }}</div>

    <div v-if="moduleSelectedMessage" class="mb-4 d-flex align-center">
      <v-icon icon="$warning"></v-icon>
      <span class="ml-2 text-red-dark body2">{{ moduleSelectedMessage }}</span>
    </div>
    <table-wrapper>
      <v-data-table-server
        :items="localValue.modules"
        :items-length="localValue.modules!.length"
        disable-pagination
        hide-default-footer
      >
        <template #headers>
          <tr>
            <th>
              <v-checkbox-btn
                density="compact"
                :indeterminate="isIndeterminate && !isAllItemsSelected"
                :model-value="isAllItemsSelected"
                @update:modelValue="handleAllRowsSelection()"
              ></v-checkbox-btn>
            </th>
            <th>{{ $t("modals.addSyslog.allModules") }}</th>
            <th class="w-25 text-right">{{ $t("modals.addSyslog.facility") }}</th>
          </tr>
        </template>
        <template #item="{ item: tableItem, index }">
          <tr>
            <td class="w-5">
              <v-checkbox-btn density="compact" v-model="tableItem.enabled"></v-checkbox-btn>
            </td>
            <td>
              <h4 class="subtitle2">
                {{ $t(`subscriptions.modules.${tableItem.module}`) }}
              </h4>
            </td>
            <td class="align-right" align="right">
              <v-select
                :placeholder="$t('connectors.psa.mappingSelectPlaceholder')"
                v-model="tableItem.facility"
                class="company-select"
                :items="facilities"
                :menu-props="{ contentClass: 'mapped-items-select' }"
                ref="mappingSelectRef"
                hide-details
                variant="plain"
              >
                <template v-slot:item="{ item, props }">
                  <v-list-item
                    :key="item.value"
                    v-bind="props"
                    width="200px"
                    @click="mappingUpdate(item.raw, index)"
                  >
                  </v-list-item>
                </template>
              </v-select>
            </td>
          </tr>
        </template>
      </v-data-table-server>
    </table-wrapper>

    <div class="subtitle1 mt-6">{{ $t("modals.addSyslog.advancedOptions") }}</div>
    <div class="body2 mb-4">{{ $t("modals.addSyslog.siemMulti") }}</div>

    <v-radio-group v-model="localValue.isMultiTenant" inline>
      <v-radio :label="$t(`general.yes`)" :value="true" class="mr-4"></v-radio>
      <v-radio :label="$t(`general.no`)" :value="false" class="mr-4"></v-radio>
    </v-radio-group>

    <v-text-field
      v-if="localValue.isMultiTenant"
      v-model="localValue.tenantLabel"
      :label="localValue.tenantLabel ? $t('modals.addSyslog.tenantLabel') : ''"
      :aria-label="localValue.tenantLabel ? $t('modals.addSyslog.tenantLabel') : ''"
      variant="outlined"
      :placeholder="$t('modals.addSyslog.tenantLabel')"
    />
  </v-form>
</template>

<script lang="ts">
import type { PropType, Ref } from "vue";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { atLeastOneIsRequired, required } from "@/_helpers/validators";
import { SubscriptionModule } from "@/constants/workplaces";
import defaultsDeep from "lodash/defaultsDeep";
import DraggableFileUploader from "@/components/DraggableFileUploader.vue";
import TableWrapper from "@/components/TableWrapper.vue";
import type { Syslog, SyslogModule } from "@/_store/connectors/syslogs.module.ts";
import { SyslogFacility } from "@/constants/syslogs.ts";
import { DataSizeUnit } from "@/constants/general.ts";
import { useI18n } from "vue-i18n";

const defaultSyslog = {
  name: "",
  destination: "",
  port: "",
  syslogProtocol: "",
  certificate: "",
  modules: [],
  isMultiTenant: false,
  tenantLabel: "",
};

export default defineComponent({
  components: {
    TableWrapper,
    DraggableFileUploader,
  },
  props: {
    config: {
      type: Object as PropType<{ item: Syslog; action: string }>,
      required: true,
    },
    showValidationAlert: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:valid", "update:localValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const valid = ref(false);
    const isFormSubmitted = ref(false);
    const localValue: Ref<Partial<Syslog>> = ref(defaultsDeep(props.config.item, defaultSyslog));
    const form = ref();

    const isTriggersControlDirty = ref(false);

    const modules = [
      SubscriptionModule.CLOUD_SECURITY,
      SubscriptionModule.EMAIL_SECURITY,
      SubscriptionModule.ENDPOINT_SECURITY,
      SubscriptionModule.USER_DATA_GOVERNANCE,
      SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
      SubscriptionModule.EDR,
    ];

    const facilities = ref(Object.values(SyslogFacility));

    emit("update:localValue", localValue.value);

    watch(valid, (value: boolean) => {
      if (value === null) {
        return emit("update:valid", true);
      }

      emit("update:valid", value);
    });

    watch(
      localValue,
      (value) => {
        emit("update:localValue", value);
      },
      { deep: true }
    );

    const validate = async () => {
      isTriggersControlDirty.value = true;

      const isValid = await form.value.validate();

      isFormSubmitted.value = true;

      return isValid.valid && !fileUploadErrorMessage.value && !moduleSelectedMessage.value;
    };

    onMounted(() => {
      localValue.value.isMultiTenant = !!props.config.item.tenantLabel;
      const modulesMapping = props.config.item.modules! || ([] as SyslogModule[]);

      localValue.value.modules = modules.map((module) => {
        const existingMapping = modulesMapping.find((item) => item.module === module);

        return {
          module,
          enabled: !!existingMapping,
          facility: existingMapping?.facility || facilities.value[0],
        };
      });
    });

    const moduleSelectedMessage = computed(() => {
      if (isFormSubmitted.value) {
        return localValue.value.modules!.some((module) => module.enabled)
          ? ""
          : i18n.t("validations.pleaseSelectAtLeastOneOption");
      }
      return "";
    });

    const fileUploadErrorMessage = computed(() => {
      if (isFormSubmitted.value) {
        if (!localValue.value.certificate) {
          return i18n.t("validations.missingFile");
        }
        if ((localValue.value.certificate as File).size > DataSizeUnit.MB) {
          return i18n.t("validations.fileSizeError");
        }
      }

      return "";
    });

    const handleFileUpload = (uploadedFile: File) => {
      isFormSubmitted.value = true;
      localValue.value.certificate = uploadedFile;
    };

    const mappingUpdate = (selectedFacility: SyslogFacility, rowIndex: number) => {
      if (!localValue.value.modules) return;
      localValue.value.modules[rowIndex].facility = selectedFacility;
    };

    const isAllItemsSelected = computed(() =>
      localValue.value.modules!.every((item) => item.enabled)
    );
    const isIndeterminate = computed(() => localValue.value.modules!.some((item) => item.enabled));
    const handleAllRowsSelection = () => {
      if (isAllItemsSelected.value) {
        localValue.value.modules!.forEach((item) => (item.enabled = false));
      } else {
        localValue.value.modules!.forEach((item) => (item.enabled = true));
      }
    };

    return {
      isTriggersControlDirty,
      valid,
      localValue,
      form,
      validate,
      required,
      atLeastOneIsRequired,
      handleFileUpload,
      modules,
      facilities,
      mappingUpdate,
      isIndeterminate,
      isAllItemsSelected,
      handleAllRowsSelection,
      fileUploadErrorMessage,
      moduleSelectedMessage,
    };
  },
});
</script>

<style lang="scss" scoped>
.trigger-group {
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;
  margin-top: 10px;
}

.trigger-border {
  border-top: 1px solid rgb(var(--v-theme-indigo-pale));
}

:deep(.trigger-group) {
  .v-selection-control--error:not(.v-selection-control--disabled) .v-label {
    color: inherit;
  }
}

.port-input {
  max-width: 160px;
}

// Mapped items table

:deep(*) {
  .v-table {
    box-shadow: none !important;
    border: 1px solid rgb(var(--v-theme-indigo-pale));
  }

  .filter-menu {
    width: 125px;
  }

  .company-select {
    width: fit-content;

    .v-field__input {
      min-width: 30px;
      margin-right: 10px;
      padding: 10px 0 !important;

      input::placeholder {
        color: rgb(var(--v-theme-link));
        opacity: 1;
      }

      .v-select__selection-text {
        color: rgb(var(--v-theme-primary));
      }
    }
  }

  .filter-menu .v-field {
    display: flex;
    align-items: center;
  }
}

:global(.v-menu > .mapped-items-select > .v-list) {
  padding-bottom: 0 !important;
}

.clear-mapping-button {
  position: sticky !important;
  bottom: 0;
  width: 100%;
  flex-direction: column;
  display: flex;
  background: white;

  > .v-btn {
    justify-content: flex-start;
    text-transform: none;
    font-weight: normal;
    font-size: inherit;
    letter-spacing: normal;
    height: 48px;
  }
}

.error-border {
  border: solid 1px rgb(var(--v-theme-red-dark));
}
</style>
